import * as React from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  desktopMedallionBadgeRecipe,
  desktopMedallionDiscRecipe,
  desktopMedallionGlowRecipe,
  desktopMedallionRecipe,
  desktopMedallionStrikeRecipe,
  desktopNoticeColumnRecipe,
  desktopNoticeGlowRecipe,
  desktopNoticeGridRecipe,
  desktopNoticeRecipe,
} from '../recipes/studio.recipe';
import { Stack } from './Layout';
import { Text } from './Text';

export interface DesktopOnlyNoticeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description: React.ReactNode;
  /** The way out — a link to the live site, usually. */
  action?: React.ReactNode;
  /** A quiet line under the action: the address it opens. */
  caption?: React.ReactNode;
}

/**
 * "Open this on a bigger screen."
 *
 *   <StudioLayout>
 *     <DesktopOnlyNotice
 *       title="Design on a bigger canvas"
 *       description="The Website Studio is built for a wider screen…"
 *       action={<Button asChild variant="outline"><a href={live}>View your live site</a></Button>}
 *       caption={liveLabel}
 *     />
 *   </StudioLayout>
 *
 * Shown in place of the studio below the `lg` breakpoint, where three panes
 * do not fit: a faint grid and a glow behind a centred column — a monitor
 * medallion with a crossed-out phone, the title, one line of why, and the
 * action. Rises into place on mount unless motion is reduced.
 */
export const DesktopOnlyNotice = React.forwardRef<
  HTMLDivElement,
  DesktopOnlyNoticeProps
>(function DesktopOnlyNotice(
  { title, description, action, caption, className, ...props },
  ref
) {
  return (
    <div ref={ref} className={cn(desktopNoticeRecipe(), className)} {...props}>
      {/* Soft brand backdrop: a faint grid texture plus a glow up top. */}
      <div aria-hidden="true" className={desktopNoticeGridRecipe()} />
      <div aria-hidden="true" className={desktopNoticeGlowRecipe()} />

      <div className={desktopNoticeColumnRecipe()}>
        {/* Hero motif: a desktop medallion with a "no phone" badge. */}
        <div className={desktopMedallionRecipe()}>
          <div aria-hidden="true" className={desktopMedallionGlowRecipe()} />
          <span className={desktopMedallionDiscRecipe()}>
            <Monitor className="size-11" strokeWidth={1.5} aria-hidden="true" />
          </span>
          <span className={desktopMedallionBadgeRecipe()}>
            <Smartphone
              className="size-icon-sm"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <span
              aria-hidden="true"
              className={desktopMedallionStrikeRecipe()}
            />
          </span>
        </div>

        <Text variant="h2">{title}</Text>
        <Text variant="body-sm" tone="muted" className="mt-3 leading-relaxed">
          {description}
        </Text>

        {action ? (
          <Stack gap={2} align="center" className="mt-8">
            {action}
            {caption ? (
              <Text
                as="span"
                variant="caption"
                tone="muted"
                truncate
                className="max-w-[16rem]"
              >
                {caption}
              </Text>
            ) : null}
          </Stack>
        ) : null}
      </div>
    </div>
  );
});
